import { useEffect, useState, useCallback, useMemo, memo } from 'react';
import {
    Dialog,
    Box,
    Typography,
    IconButton,
    TextField,
    Button,
    Autocomplete,
    Chip,
    CircularProgress,
    MenuItem,
    ToggleButton,
    ToggleButtonGroup,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import GetCookieByName from '../../hooks/GetCookie';
import {
    useCreateBrief, useUpdateBrief, useUserLookup, useCategoryLookup, useBrandLookup,
    useUploadBriefAttachments, useUploadDraftAttachments, useDeleteDraftAttachments,
} from '../../hooks/useBriefs';
import { useSnackbar } from 'notistack';
import {
    validateBriefTitle, validateBudget,
    validateAssignee, validateReviewer, validateDeadline, validateDescription,
    hasErrors,
} from './validation';
import { DELIVERABLE_TYPE_OPTIONS, PRIORITY_OPTIONS, STATUS_OPTIONS } from './briefOptions';
import { attachmentFileName, labelForStatus } from './briefUiUtils';

const fieldSx = {
    mb: 3,
    '& .MuiOutlinedInput-root': {
        background: '#F9FAFB',
        borderRadius: '10px',
        fontSize: 14.5,
        '& fieldset': { borderColor: '#E5E7EB' },
        '&:hover fieldset': { borderColor: '#D1D5DB' },
        '&.Mui-focused fieldset': { borderColor: '#FF5200', borderWidth: '1.5px' },
        '&.Mui-error fieldset': { borderColor: '#EF4444' },
    },
    '& input::placeholder': { color: '#9CA3AF', opacity: 1 },
};

const fieldLabelSx = {
    mb: 1,
    color: '#374151',
    fontWeight: 600,
    fontSize: 13.5,
};

const toDateInputValue = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toISOString().slice(0, 10);
};

// Kept in sync with MAX_ATTACHMENTS/MAX_ATTACHMENT_SIZE_BYTES in
// pluginbackendnode/models/brief.js and helpers/s3BriefAttachments.js — this
// is just the early, friendlier check; the backend enforces the real limits.
const MAX_ATTACHMENTS = 5;
const MAX_ATTACHMENT_SIZE_MB = 5;

const findInvalidFile = (files) => {
    for (const file of files) {
        if (!file.type || !file.type.startsWith('image/')) return `"${file.name}" isn't an image`;
        if (file.size > MAX_ATTACHMENT_SIZE_MB * 1024 * 1024) return `"${file.name}" is larger than ${MAX_ATTACHMENT_SIZE_MB}MB`;
    }
    return null;
};

/**
 * @param {boolean} open
 * @param {() => void} onClose
 * @param {(data: any) => void} onSaved
 * @param {object|null} initialBrief - when provided, the modal edits this brief instead of creating a new one.
 * @param {boolean} isSuperUser - a super user gets full edit access regardless of their relation to the brief.
 */
const CreateBriefsModal = ({ open, onClose, onSaved, initialBrief = null, isSuperUser = false }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isEditing = Boolean(initialBrief);

    // State
    const [title, setTitle] = useState('');
    const [brand, setBrand] = useState(null);
    const [deliverableType, setDeliverableType] = useState('');
    const [deadline, setDeadline] = useState('');
    const [priority, setPriority] = useState('Medium');
    const [budget, setBudget] = useState('');
    const [assignee, setAssignee] = useState([]);
    const [reviewer, setReviewer] = useState([]);
    const [description, setDescription] = useState('');
    const [referenceLinks, setReferenceLinks] = useState([]);
    const [referenceLinkInput, setReferenceLinkInput] = useState('');
    const [category, setCategory] = useState([]);
    const [attachments, setAttachments] = useState([]);
    const [status, setStatus] = useState('Pending');
    const [touched, setTouched] = useState({
        title: false, budget: false,
        assignee: false, reviewer: false, deadline: false, description: false,
    });
    const [serverErrors, setServerErrors] = useState({});

    const { mutate: createBrief, isPending: isCreating } = useCreateBrief();
    const { mutate: updateBrief, isPending: isUpdating } = useUpdateBrief();
    const uploadAttachments = useUploadBriefAttachments();
    const uploadDraftAttachments = useUploadDraftAttachments();
    const deleteDraftAttachments = useDeleteDraftAttachments();
    const isUploadingAttachments = uploadAttachments.isPending || uploadDraftAttachments.isPending;
    const isPending = isCreating || isUpdating;
    const { data: users = [], isLoading: loadingUsers } = useUserLookup();
    const { data: categories = [], isLoading: loadingCategories } = useCategoryLookup();
    const { data: brandOptions = [], isLoading: loadingBrands } = useBrandLookup();
    const { enqueueSnackbar } = useSnackbar();

    // The brief's creator defaults into the Reviewer field — computed once from the
    // login cookie (see Signin.jsx's saveLoginData) rather than waited-on from the
    // users lookup, so it's available immediately when the create modal opens.
    const currentUser = useMemo(() => {
        try {
            const parsed = JSON.parse(GetCookieByName('user'));
            return parsed?._id ? { id: parsed._id, name: parsed.name || '' } : null;
        } catch {
            return null;
        }
    }, []);

    // Floors the native date input's picker at today — computed once per mount,
    // not per render, so a modal left open overnight doesn't shift mid-session.
    const todayDateValue = useMemo(() => toDateInputValue(new Date()), []);

    // The brief's own stored deadline, in the same shape as the `deadline`
    // state — lets validateDeadline tell "still the original value" apart from
    // "changed to a new past date" (see that function's comment).
    const originalDeadlineValue = useMemo(
        () => (isEditing ? toDateInputValue(initialBrief.deadline) : null),
        [isEditing, initialBrief]
    );

    // A plain assignee (not also the creator, a reviewer, or a super user) can
    // only add to the assignee list and change status — every other field is
    // locked to them. Mirrors the same restriction enforced server-side in
    // routes/briefs.js's PATCH handler, so this is UX only, not the real gate.
    const isAssigneeOnly = useMemo(() => {
        if (!isEditing || !currentUser || isSuperUser) return false;
        const uid = currentUser.id;
        const isCreator = initialBrief.createdBy?.id === uid;
        const isReviewer = (initialBrief.reviewer || []).some((r) => r.id === uid);
        const isAssignee = (initialBrief.assignee || []).some((a) => a.id === uid);
        return isAssignee && !isCreator && !isReviewer;
    }, [isEditing, initialBrief, currentUser, isSuperUser]);

    // On top of isAssigneeOnly's general field lock, a Delayed brief's status
    // specifically can only be moved by the creator/reviewer/super user.
    const statusLockedForAssignee = isAssigneeOnly && initialBrief?.status === 'Delayed';

    // Assignees present before this session started — an assignee-only editor
    // may add new ones but not remove these (renderTags below omits the
    // delete affordance for exactly this set).
    const originalAssigneeIds = useMemo(
        () => new Set((initialBrief?.assignee || []).map((a) => a.id)),
        [initialBrief]
    );

    // Memoized validation results
    const clientErrors = useMemo(() => ({
        title: validateBriefTitle(title),
        budget: validateBudget(budget),
        assignee: validateAssignee(assignee),
        reviewer: validateReviewer(reviewer),
        deadline: validateDeadline(deadline, originalDeadlineValue),
        description: validateDescription(description),
    }), [title, budget, assignee, reviewer, deadline, description, originalDeadlineValue]);

    const isValid = useMemo(() => !hasErrors(clientErrors), [clientErrors]);

    const fieldError = useCallback((field) =>
        touched[field] ? serverErrors[field] || clientErrors[field] : null,
    [touched, serverErrors, clientErrors]);

    // Reset form
    const resetForm = useCallback(() => {
        setTitle('');
        setBrand(null);
        setDeliverableType('');
        setDeadline('');
        setPriority('Medium');
        setBudget('');
        setAssignee([]);
        setReviewer(currentUser ? [currentUser] : []);
        setDescription('');
        setReferenceLinks([]);
        setReferenceLinkInput('');
        setCategory([]);
        setAttachments([]);
        setStatus('Pending');
        setTouched({
            title: false, budget: false,
            assignee: false, reviewer: false, deadline: false, description: false,
        });
        setServerErrors({});
    }, [currentUser]);

    // Populate the form from initialBrief when opening in edit mode.
    useEffect(() => {
        if (!open) return;

        if (initialBrief) {
            setTitle(initialBrief.title || '');
            setBrand(initialBrief.brand || null);
            setDeliverableType(initialBrief.deliverableType || '');
            setDeadline(toDateInputValue(initialBrief.deadline));
            setPriority(initialBrief.priority || 'Medium');
            setBudget(initialBrief.budget ?? '');
            setAssignee(initialBrief.assignee || []);
            setReviewer(initialBrief.reviewer || []);
            setDescription(initialBrief.description || '');
            setReferenceLinks(initialBrief.referenceLinks || []);
            setReferenceLinkInput('');
            setCategory(initialBrief.category || []);
            setAttachments(initialBrief.attachments || []);
            setStatus(initialBrief.status || 'Pending');
        } else {
            resetForm();
        }
    }, [open, initialBrief, resetForm]);

    // Close handler — no cleanup here on purpose. Used after a successful
    // Save (see mutationOptions.onSuccess below), where any draft attachments
    // just got persisted onto the new brief and must NOT be deleted.
    const handleClose = useCallback(() => {
        resetForm();
        onClose();
    }, [resetForm, onClose]);

    // Cancelling instead of saving — used by the X button, backdrop/Escape,
    // and the Cancel button. If this was a brand-new brief with draft
    // attachments already uploaded to S3, they never made it into any saved
    // brief's attachments array, so they'd otherwise sit there forever.
    // Fire-and-forget: the modal closes regardless of whether this succeeds.
    const handleCancel = useCallback(() => {
        if (!isEditing && attachments.length > 0) {
            deleteDraftAttachments.mutate(attachments);
        }
        handleClose();
    }, [isEditing, attachments, deleteDraftAttachments, handleClose]);

    // Save handler (create or update)
    const handleSave = useCallback(() => {
        setTouched({
            title: true, budget: true,
            assignee: true, reviewer: true, deadline: true, description: true,
        });
        if (!isValid) return;

        // Flush a link that's typed but not yet committed with Enter — otherwise
        // clicking Save immediately after typing silently drops it.
        const pendingLink = referenceLinkInput.trim();
        const finalReferenceLinks = pendingLink ? [...referenceLinks, pendingLink] : referenceLinks;
        if (pendingLink) {
            setReferenceLinks(finalReferenceLinks);
            setReferenceLinkInput('');
        }

        const payload = {
            title: title.trim(),
            // Explicit null (not undefined) when unset — undefined keys drop out of
            // the JSON body entirely, which would silently fail to clear an existing
            // brand selection when editing.
            brand: brand?.id || null,
            deliverableType: deliverableType || null,
            deadline: deadline || null,
            priority,
            budget: budget === '' ? null : Number(budget),
            assignee: assignee.map((u) => u.id),
            reviewer: reviewer.map((u) => u.id),
            description: description.trim(),
            referenceLinks: finalReferenceLinks,
            category: category.map((c) => c.id),
            // Uploads persist immediately (see handleFilesSelected); this only
            // carries a removal forward — attachments can shrink here, never grow.
            attachments,
            status,
        };

        const mutationOptions = {
            onSuccess: (data) => {
                enqueueSnackbar(isEditing ? 'Brief updated' : 'Brief created', { variant: 'success' });
                onSaved?.(data);
                handleClose();
            },
            onError: (error) => {
                const apiFieldErrors = error?.response?.data?.errors;
                if (apiFieldErrors && typeof apiFieldErrors === 'object') {
                    setServerErrors(apiFieldErrors);
                }
                const message = error?.response?.data?.message ||
                               `Couldn't ${isEditing ? 'update' : 'create'} the brief. Try again.`;
                enqueueSnackbar(message, { variant: 'error' });
            },
        };

        if (isEditing) {
            updateBrief({ briefId: initialBrief.id, ...payload }, mutationOptions);
        } else {
            createBrief(payload, mutationOptions);
        }
    }, [
        isValid, title, brand, deliverableType, deadline, priority, budget,
        assignee, reviewer, description, referenceLinks, referenceLinkInput, category, attachments, status,
        isEditing, initialBrief, createBrief, updateBrief, enqueueSnackbar, onSaved, handleClose,
    ]);

    const handleTitleChange = useCallback((e) => {
        setTitle(e.target.value);
        if (serverErrors.title) setServerErrors((prev) => ({ ...prev, title: null }));
    }, [serverErrors.title]);

    const handleBudgetChange = useCallback((e) => {
        const value = e.target.value;
        if (value === '' || /^[0-9]*$/.test(value)) setBudget(value);
    }, []);

    const handleCopyLink = useCallback((link) => {
        navigator.clipboard.writeText(link)
            .then(() => enqueueSnackbar('Link copied to clipboard', { variant: 'success' }))
            .catch(() => enqueueSnackbar('Could not copy link', { variant: 'error' }));
    }, [enqueueSnackbar]);

    // Editing an existing brief: uploads persist immediately (there's a real
    // briefId to key S3 objects and the DB array under). Creating a new one:
    // there's no id yet, so files still go straight to S3 (scoped to this
    // user's own drafts folder server-side) but the resulting URLs just sit in
    // local `attachments` state until Save actually creates the brief and
    // sends them along.
    const handleFilesSelected = useCallback((e) => {
        const files = Array.from(e.target.files || []);
        e.target.value = ''; // lets the same file be picked again later
        if (!files.length) return;

        if (attachments.length + files.length > MAX_ATTACHMENTS) {
            enqueueSnackbar(`Up to ${MAX_ATTACHMENTS} images per brief`, { variant: 'error' });
            return;
        }
        const invalidReason = findInvalidFile(files);
        if (invalidReason) {
            enqueueSnackbar(invalidReason, { variant: 'error' });
            return;
        }

        if (isEditing) {
            uploadAttachments.mutate(
                { briefId: initialBrief.id, files },
                {
                    onSuccess: (data) => {
                        setAttachments(data.attachments || []);
                        enqueueSnackbar('Attachment(s) uploaded', { variant: 'success' });
                    },
                    onError: (error) => {
                        enqueueSnackbar(
                            error?.response?.data?.message || 'Failed to upload attachment',
                            { variant: 'error' }
                        );
                    },
                }
            );
        } else {
            uploadDraftAttachments.mutate(files, {
                onSuccess: (urls) => {
                    setAttachments((prev) => [...prev, ...urls]);
                    enqueueSnackbar('Attachment(s) uploaded', { variant: 'success' });
                },
                onError: (error) => {
                    enqueueSnackbar(
                        error?.response?.data?.message || 'Failed to upload attachment',
                        { variant: 'error' }
                    );
                },
            });
        }
    }, [isEditing, initialBrief, attachments.length, uploadAttachments, uploadDraftAttachments, enqueueSnackbar]);

    const handleRemoveAttachment = useCallback((url) => {
        setAttachments((prev) => prev.filter((a) => a !== url));
    }, []);

    return (
        <Dialog
            open={open}
            onClose={handleCancel}
            fullWidth
            maxWidth="sm"
            transitionDuration={{ enter: 260, exit: 180 }}
            PaperProps={{
                elevation: 0,
                sx: {
                    borderRadius: isMobile ? 0 : '20px',
                    overflow: 'hidden',
                    border: '1px solid #E5E7EB',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.06)',
                    m: isMobile ? 0 : 2,
                    maxHeight: isMobile ? '100vh' : '90vh',
                },
            }}
        >
            {/* Orange top stripe */}
            <Box sx={{ height: 4, background: 'linear-gradient(90deg, #FF5200 0%, #FF8C00 100%)', flexShrink: 0 }} />

            {/* Header */}
            <Box
                sx={{
                    px: { xs: 2.5, sm: 3 },
                    pt: 2.5,
                    pb: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderBottom: '1px solid #F3F4F6',
                    background: '#fff',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                    <Box
                        sx={{
                            width: 38, height: 38, borderRadius: '10px',
                            background: 'linear-gradient(135deg, rgba(255,82,0,0.1), rgba(255,140,0,0.06))',
                            border: '1px solid rgba(255,82,0,0.18)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                        }}
                    >
                        <AssignmentOutlinedIcon sx={{ color: '#FF5200', fontSize: 18 }} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ color: '#111827', fontWeight: 700, fontSize: 16, lineHeight: 1.2 }}>
                            {isEditing ? 'Edit Brief' : 'Create Brief'}
                        </Typography>
                        <Typography sx={{ color: '#9CA3AF', fontSize: 12, mt: 0.25 }}>
                            Scope, deliverables, deadline and ownership for a new brief
                        </Typography>
                    </Box>
                </Box>
                <IconButton
                    size="small"
                    onClick={handleCancel}
                    sx={{
                        color: '#9CA3AF',
                        background: '#F9FAFB',
                        border: '1px solid #E5E7EB',
                        flexShrink: 0,
                        '&:hover': { background: '#F3F4F6', color: '#374151' },
                    }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>

            {/* Body */}
            <Box sx={{ px: { xs: 2.5, sm: 3 }, pt: 2.5, pb: 1, background: '#fff', overflowY: 'auto' }}>
                {isAssigneeOnly && (
                    <Typography sx={{ fontSize: 12.5, color: '#9CA3AF', mb: 2, fontStyle: 'italic' }}>
                        You can only add assignees and update status on this brief.
                    </Typography>
                )}

                <Typography sx={fieldLabelSx}>Brief Title *</Typography>
                <TextField
                    fullWidth
                    placeholder="e.g. Monsoon Skincare Launch"
                    value={title}
                    onChange={handleTitleChange}
                    onBlur={() => setTouched((prev) => ({ ...prev, title: true }))}
                    error={Boolean(fieldError('title'))}
                    helperText={fieldError('title')}
                    inputProps={{ maxLength: 200 }}
                    disabled={isAssigneeOnly}
                    sx={fieldSx}
                />

                <Typography sx={fieldLabelSx}>Brand</Typography>
                <Autocomplete
                    options={brandOptions}
                    getOptionLabel={(b) => b?.name ?? ''}
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    value={brand}
                    onChange={(_, value) => {
                        setBrand(value);
                        if (serverErrors.brand) setServerErrors((prev) => ({ ...prev, brand: null }));
                    }}
                    loading={loadingBrands}
                    disabled={loadingBrands || isAssigneeOnly}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder="Select brand"
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {loadingBrands && <CircularProgress size={16} sx={{ color: '#FF5200' }} />}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                        />
                    )}
                    sx={fieldSx}
                />

                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Box sx={{ flex: 1 }}>
                        <Typography sx={fieldLabelSx}>Deliverable Type</Typography>
                        <TextField
                            select
                            fullWidth
                            value={deliverableType}
                            onChange={(e) => setDeliverableType(e.target.value)}
                            disabled={isAssigneeOnly}
                            sx={fieldSx}
                        >
                            <MenuItem value="">
                                <em>None</em>
                            </MenuItem>
                            {DELIVERABLE_TYPE_OPTIONS.map((opt) => (
                                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                            ))}
                        </TextField>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography sx={fieldLabelSx}>Deadline *</Typography>
                        <TextField
                            type="date"
                            fullWidth
                            value={deadline}
                            onChange={(e) => setDeadline(e.target.value)}
                            onBlur={() => setTouched((prev) => ({ ...prev, deadline: true }))}
                            error={Boolean(fieldError('deadline'))}
                            helperText={fieldError('deadline')}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ min: todayDateValue }}
                            disabled={isAssigneeOnly}
                            sx={fieldSx}
                        />
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Box sx={{ flex: 1 }}>
                        <Typography sx={fieldLabelSx}>Priority</Typography>
                        <TextField
                            select
                            fullWidth
                            value={priority}
                            onChange={(e) => setPriority(e.target.value)}
                            disabled={isAssigneeOnly}
                            sx={fieldSx}
                        >
                            {PRIORITY_OPTIONS.map((opt) => (
                                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                            ))}
                        </TextField>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography sx={fieldLabelSx}>Budget (₹)</Typography>
                        <TextField
                            fullWidth
                            placeholder="850000"
                            value={budget}
                            onChange={handleBudgetChange}
                            onBlur={() => setTouched((prev) => ({ ...prev, budget: true }))}
                            error={Boolean(fieldError('budget'))}
                            helperText={fieldError('budget') || 'numbers only'}
                            disabled={isAssigneeOnly}
                            sx={fieldSx}
                        />
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Box sx={{ flex: 1 }}>
                        <Typography sx={fieldLabelSx}>Assignee *</Typography>
                        <Autocomplete
                            multiple
                            options={users}
                            getOptionLabel={(u) => u?.name ?? ''}
                            isOptionEqualToValue={(a, b) => a.id === b.id}
                            value={assignee}
                            onChange={(_, newValue) => {
                                // An assignee-only editor can add to this list but not
                                // remove from it — blocks both the (hidden, see
                                // renderTags) delete icon and Backspace-to-remove.
                                if (isAssigneeOnly) {
                                    const droppedOriginal = [...originalAssigneeIds].some(
                                        (id) => !newValue.some((v) => v.id === id)
                                    );
                                    if (droppedOriginal) return;
                                }
                                setAssignee(newValue);
                                setTouched((prev) => ({ ...prev, assignee: true }));
                            }}
                            onBlur={() => setTouched((prev) => ({ ...prev, assignee: true }))}
                            loading={loadingUsers}
                            disabled={loadingUsers}
                            renderTags={(value, getTagProps) =>
                                value.map((option, index) => {
                                    const { key, onDelete, ...chipProps } = getTagProps({ index });
                                    const isLocked = isAssigneeOnly && originalAssigneeIds.has(option.id);
                                    return (
                                        <Chip
                                            key={key}
                                            {...chipProps}
                                            onDelete={isLocked ? undefined : onDelete}
                                            label={option.name}
                                        />
                                    );
                                })
                            }
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder={assignee.length ? '' : 'Select assignee'}
                                    error={Boolean(fieldError('assignee'))}
                                    helperText={fieldError('assignee')}
                                />
                            )}
                            sx={fieldSx}
                        />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography sx={fieldLabelSx}>Reviewer *</Typography>
                        <Autocomplete
                            multiple
                            options={users}
                            getOptionLabel={(u) => u?.name ?? ''}
                            isOptionEqualToValue={(a, b) => a.id === b.id}
                            value={reviewer}
                            onChange={(_, value) => {
                                setReviewer(value);
                                setTouched((prev) => ({ ...prev, reviewer: true }));
                            }}
                            onBlur={() => setTouched((prev) => ({ ...prev, reviewer: true }))}
                            loading={loadingUsers}
                            disabled={loadingUsers || isAssigneeOnly}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder={reviewer.length ? '' : 'Select reviewer'}
                                    error={Boolean(fieldError('reviewer'))}
                                    helperText={fieldError('reviewer')}
                                />
                            )}
                            sx={fieldSx}
                        />
                    </Box>
                </Box>

                <Typography sx={fieldLabelSx}>Description *</Typography>
                <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    placeholder="Scope, deliverables, channels, key context..."
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    onBlur={() => setTouched((prev) => ({ ...prev, description: true }))}
                    error={Boolean(fieldError('description'))}
                    helperText={fieldError('description')}
                    disabled={isAssigneeOnly}
                    sx={fieldSx}
                />

                <Typography sx={fieldLabelSx}>Reference Links</Typography>
                <Autocomplete
                    multiple
                    freeSolo
                    options={[]}
                    value={referenceLinks}
                    inputValue={referenceLinkInput}
                    disabled={isAssigneeOnly}
                    onInputChange={(_, newValue, reason) => setReferenceLinkInput(reason === 'reset' ? '' : newValue)}
                    onChange={(_, value) => {
                        setReferenceLinks(value);
                        setReferenceLinkInput('');
                    }}
                    renderTags={(value, getTagProps) =>
                        value.map((link, index) => {
                            const { key, ...chipProps } = getTagProps({ index });
                            return (
                                <Chip
                                    key={key}
                                    {...chipProps}
                                    label={link}
                                    icon={<ContentCopyIcon sx={{ fontSize: '14px !important' }} />}
                                    onClick={() => handleCopyLink(link)}
                                    sx={{ cursor: 'pointer', maxWidth: 260 }}
                                />
                            );
                        })
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder="https://..."
                            helperText="Press Enter to add · click a link to copy it"
                            onBlur={() => {
                                const pending = referenceLinkInput.trim();
                                if (pending) {
                                    setReferenceLinks((prev) => [...prev, pending]);
                                    setReferenceLinkInput('');
                                }
                            }}
                        />
                    )}
                    sx={fieldSx}
                />

                <Typography sx={fieldLabelSx}>Category</Typography>
                <Autocomplete
                    multiple
                    options={categories}
                    getOptionLabel={(c) => c?.name ?? ''}
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    value={category}
                    onChange={(_, value) => setCategory(value)}
                    loading={loadingCategories}
                    disabled={loadingCategories || isAssigneeOnly}
                    renderInput={(params) => (
                        <TextField {...params} placeholder="Select category" />
                    )}
                    sx={fieldSx}
                />

                <Typography sx={fieldLabelSx}>Attachments ({attachments.length}/{MAX_ATTACHMENTS})</Typography>
                <Box sx={{ mb: 3 }}>
                    <Button
                        component="label"
                        variant="outlined"
                        size="small"
                        startIcon={isUploadingAttachments ? <CircularProgress size={14} /> : <AttachFileIcon />}
                        disabled={isUploadingAttachments || attachments.length >= MAX_ATTACHMENTS || isAssigneeOnly}
                        sx={{ textTransform: 'none', borderRadius: '10px', mb: 0.75, borderColor: '#E5E7EB', color: '#374151' }}
                    >
                        {isUploadingAttachments ? 'Uploading…' : 'Upload images'}
                        <input type="file" multiple accept="image/*" hidden onChange={handleFilesSelected} />
                    </Button>
                    <Typography sx={{ fontSize: 11.5, color: '#9CA3AF', mb: 1.5 }}>
                        Images only, up to {MAX_ATTACHMENT_SIZE_MB}MB each, {MAX_ATTACHMENTS} per brief.
                    </Typography>
                    {attachments.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {attachments.map((url) => (
                                <Chip
                                    key={url}
                                    icon={<InsertDriveFileOutlinedIcon sx={{ fontSize: '15px !important' }} />}
                                    label={attachmentFileName(url)}
                                    onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}
                                    onDelete={isAssigneeOnly ? undefined : () => handleRemoveAttachment(url)}
                                    sx={{ cursor: 'pointer', maxWidth: 240 }}
                                />
                            ))}
                        </Box>
                    )}
                </Box>

                <Typography sx={fieldLabelSx}>Status</Typography>
                <ToggleButtonGroup
                    exclusive
                    fullWidth
                    value={status}
                    onChange={(_, value) => value && setStatus(value)}
                    sx={{ mb: 1 }}
                    disabled={statusLockedForAssignee}
                >
                    {STATUS_OPTIONS.map((opt) => (
                        <ToggleButton
                            key={opt}
                            value={opt}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 600,
                                '&.Mui-selected': {
                                    background: 'linear-gradient(135deg, #FF5200 0%, #FF8C00 100%)',
                                    color: '#fff',
                                    '&:hover': { background: 'linear-gradient(135deg, #FF5200 0%, #FF8C00 100%)' },
                                },
                            }}
                        >
                            {labelForStatus(opt)}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
                {statusLockedForAssignee && (
                    <Typography sx={{ fontSize: 12, color: '#9CA3AF', mt: -0.5, mb: 1 }}>
                        Only the creator, a reviewer, or a super user can move this out of Delayed.
                    </Typography>
                )}
            </Box>

            {/* Footer */}
            <Box
                sx={{
                    px: { xs: 2.5, sm: 3 }, py: 2,
                    borderTop: '1px solid #F3F4F6',
                    display: 'flex',
                    gap: 1.5,
                    background: '#FAFAFA',
                }}
            >
                <Button
                    fullWidth
                    onClick={handleCancel}
                    sx={{
                        color: '#6B7280', background: '#fff', borderRadius: '10px',
                        py: 1.1, fontSize: 14, fontWeight: 500, textTransform: 'none',
                        border: '1px solid #E5E7EB', boxShadow: 'none',
                        '&:hover': { background: '#F9FAFB', borderColor: '#D1D5DB', color: '#374151' },
                    }}
                >
                    Cancel
                </Button>
                <Button
                    fullWidth
                    variant="contained"
                    disabled={isPending || !isValid}
                    onClick={handleSave}
                    sx={{
                        background: 'linear-gradient(135deg, #FF5200 0%, #FF8C00 100%)',
                        borderRadius: '10px', py: 1.1, fontSize: 14, fontWeight: 700,
                        textTransform: 'none', boxShadow: '0 4px 14px rgba(255,82,0,0.3)',
                        '&:hover': {
                            background: 'linear-gradient(135deg, #FF5200 0%, #FF8C00 100%)',
                            opacity: 0.9, boxShadow: '0 6px 20px rgba(255,82,0,0.45)',
                        },
                        '&.Mui-disabled': { background: '#F3F4F6', color: '#9CA3AF', boxShadow: 'none' },
                    }}
                >
                    {isPending ? <CircularProgress size={20} color="inherit" /> : (isEditing ? 'Save Changes' : 'Create Brief')}
                </Button>
            </Box>
        </Dialog>
    );
};

export default memo(CreateBriefsModal);
